import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    formatDraftOptionLabel,
    getDraftTitle,
    isHiddenFromVersions
} from '../../utils/scheduleDraftKind';
import Button from '../ui/Button';
import HelpIcon from '../Documentation/HelpIcon';

import ScheduleOptionsMenu from '../ScheduleOptionsMenu';
import { IoCalendarClearOutline, IoPersonOutline, IoHomeOutline, IoShareSocialOutline, IoChevronDown, IoSearchOutline } from 'react-icons/io5';
import { IoMdBook, IoMdMicrophone } from 'react-icons/io';
import { FaRegClock } from 'react-icons/fa';

const ClassSchedulerHeader = ({
    rooms,
    currentDraftName,
    currentDraftLabel = null,
    isAdmin,
    selectedYearForView,
    isImporting,
    isExporting,
    importError,
    importSuccess,
    isLoading,
    error,
    success,
    setError,
    setSuccess,
    setIsLoading,
    draftManager,
    onToggleDebug,
    onModeChange,
    onDraftChange,
    onSaveCurrent,
    onPublishDraft,
    onImport,
    onExport,
    currentView,
    setCurrentView,
    timePeriods,
    selectedPeriodId,
    onPeriodChange,
    textFilterQuery,
    setTextFilterQuery,
    allRoomsForGrid,
    selectedRoomForView,
    setSelectedRoomForView,
    roomVisibilityFilter = 'all',
    setRoomVisibilityFilter,
    teachers,
    selectedTeacherForView,
    setSelectedTeacherForView,
    entryTypeFilters,
    setEntryTypeFilters
}) => {
    const [isSearchOpen, setIsSearchOpen] = useState(Boolean(textFilterQuery));
    const searchInputRef = useRef(null);

    const entryTypeOptions = useMemo(() => ([
        { key: 'class', label: 'Classes', Icon: IoMdBook },
        { key: 'meeting', label: 'Meetings', Icon: FaRegClock },
        { key: 'event', label: 'Events', Icon: IoMdMicrophone }
    ]), []);

    useEffect(() => {
        if (textFilterQuery) {
            setIsSearchOpen(true);
        }
    }, [textFilterQuery]);

    useEffect(() => {
        if (isSearchOpen) {
            searchInputRef.current?.focus();
        }
    }, [isSearchOpen]);

    const handleToggleEntryType = (typeKey) => {
        if (typeof setEntryTypeFilters !== 'function') return;
        setEntryTypeFilters((prev) => {
            const current = prev ?? {};
            return {
                ...current,
                [typeKey]: !(current?.[typeKey] ?? true)
            };
        });
    };

    return (
        <div className="flex-none p-4 bg-white border-b border-gray-200 shadow-sm">
            {rooms.length > 0 ? (
                <>
                    <div className={`relative flex flex-wrap items-center gap-4 min-h-[2.75rem] ${currentView === 'room' || currentView === 'teacher' ? 'mb-4' : ''}`}>
                        {currentView !== 'calendar' && currentView !== 'shares' ? (
                            <div className="flex items-center gap-3 z-10">
                                <div className="flex items-center gap-2">
                                    <DraftModeDropdown
                                        currentDraftName={currentDraftName}
                                        draftManager={draftManager}
                                        onModeChange={onModeChange}
                                        onDraftChange={onDraftChange}
                                        timePeriods={timePeriods}
                                        selectedPeriodId={selectedPeriodId}
                                        selectedYearForView={selectedYearForView}
                                        setError={setError}
                                        setSuccess={setSuccess}
                                        setIsLoading={setIsLoading}
                                    />

                                    <HelpIcon
                                        documentationId="schedule-draft-live-mode"
                                        size={16}
                                        position="inline"
                                        className="ml-1"
                                    />
                                </div>
                            </div>
                        ) : null}

                        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center z-0">
                            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => {
                                        setCurrentView('calendar');
                                    }}
                                    className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'calendar'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    <IoCalendarClearOutline className="w-4 h-4" />
                                    Calendar View
                                </button>
                                <button
                                    onClick={() => {
                                        setCurrentView('room');
                                    }}
                                    className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'room'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    <IoHomeOutline className="w-4 h-4" />
                                    Room View
                                </button>
                                <button
                                    onClick={() => {
                                        setCurrentView('teacher');
                                    }}
                                    className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'teacher'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    <IoPersonOutline className="w-4 h-4" />
                                    Teacher View
                                </button>
                                <button
                                    onClick={() => {
                                        setCurrentView('shares');
                                    }}
                                    className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'shares'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    <IoShareSocialOutline className="w-4 h-4" />
                                    Shared Versions
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-3 ml-auto z-10">
                            {currentView !== 'shares' && (
                            <RoomVisibilityFilterDropdown
                                roomVisibilityFilter={roomVisibilityFilter}
                                setRoomVisibilityFilter={setRoomVisibilityFilter}
                            />
                            )}
                            {currentView !== 'shares' && (
                            <div className="relative flex items-center">
                                {isSearchOpen ? (
                                    <div className="relative w-80">
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={textFilterQuery}
                                            onChange={(e) => setTextFilterQuery(e.target.value)}
                                            placeholder="Filter: groups, teacher, subject, description, host"
                                            className="w-full px-3 py-2 pr-28 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div className="absolute inset-y-0 right-1 flex items-center gap-1">
                                            {entryTypeOptions.map(({ key, label, Icon }) => {
                                                const isActive = entryTypeFilters?.[key] ?? true;
                                                return (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        onClick={() => handleToggleEntryType(key)}
                                                        className={`p-1 text-sm rounded-md transition-colors flex items-center justify-center ${isActive
                                                            ? 'text-blue-700'
                                                            : 'text-gray-400 hover:text-gray-600'}`}
                                                        aria-pressed={isActive}
                                                        aria-label={label}
                                                        title={`Toggle on/off ${label.toLowerCase()}`}
                                                    >
                                                        <Icon className="w-4 h-4" aria-hidden="true" />
                                                        <span className="sr-only">{label}</span>
                                                    </button>
                                                );
                                            })}
                                            <button
                                                onClick={() => {
                                                    setTextFilterQuery('');
                                                    setIsSearchOpen(false);
                                                }}
                                                className="p-2 text-gray-400 hover:text-gray-600"
                                                aria-label="Close filter"
                                                type="button"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setIsSearchOpen(true)}
                                        className={`p-2 rounded-lg border transition-colors ${textFilterQuery
                                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                                            : 'border-gray-300 text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                                        aria-label="Open filter"
                                        title="Open filter"
                                    >
                                        <IoSearchOutline className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                            )}

                            {isAdmin && (
                                <ScheduleOptionsMenu
                                    currentYear={selectedYearForView}
                                    currentDraftName={currentDraftLabel || currentDraftName}
                                    onModeChange={onModeChange}
                                    onDraftChange={onDraftChange}
                                    onSaveCurrent={onSaveCurrent}
                                    onPublishDraft={onPublishDraft}
                                    onImport={onImport}
                                    onExport={onExport}
                                    isImporting={isImporting}
                                    isExporting={isExporting}
                                    importError={importError}
                                    importSuccess={importSuccess}
                                    isLoading={isLoading}
                                    error={error}
                                    success={success}
                                    selectedPeriodId={selectedPeriodId}
                                    timePeriods={timePeriods}
                                    setError={setError}
                                    setSuccess={setSuccess}
                                    setIsLoading={setIsLoading}
                                    draftManager={draftManager}
                                    onToggleDebug={onToggleDebug}
                                />
                            )}
                        </div>
                    </div>

                    {(currentView === 'room' || currentView === 'teacher') && (
                        <div className="pt-3 border-t border-gray-200">
                            <div className="flex items-center gap-4">
                                {currentView === 'room' && (
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700">Room:</label>
                                        <select
                                            value={typeof selectedRoomForView === 'object' && selectedRoomForView !== null ? selectedRoomForView.name : (selectedRoomForView || '')}
                                            onChange={(e) => {
                                                const selectedRoom = allRoomsForGrid.find(room => room.name === e.target.value);
                                                setSelectedRoomForView(selectedRoom || e.target.value);
                                            }}
                                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-48"
                                        >
                                            <option value="">Select a room</option>
                                            {allRoomsForGrid.map(room => (
                                                <option key={room.id || room.name} value={room.name}>{room.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {currentView === 'teacher' && (
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700">Teacher:</label>
                                        <select
                                            value={selectedTeacherForView || ''}
                                            onChange={(e) => setSelectedTeacherForView(e.target.value)}
                                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-48"
                                        >
                                            <option value="">Select a teacher</option>
                                            {teachers.map(teacher => (
                                                <option key={teacher.id} value={teacher.name}>{teacher.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="flex justify-between items-center">
                    <h1 className="text-xl font-semibold text-gray-800">Schedule Setup</h1>
                </div>
            )}
        </div>
    );
};

export default ClassSchedulerHeader;

const ROOM_VISIBILITY_OPTIONS = [
    { value: 'all', label: 'Show all rooms' },
    { value: 'visible', label: 'Show visible rooms only' },
    { value: 'hidden', label: 'Show hidden rooms only' }
];

const RoomFilterIcon = ({ className = 'w-5 h-5' }) => (
    <svg
        viewBox="0 0 24 24"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
    >
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
);

const RoomVisibilityFilterDropdown = ({ roomVisibilityFilter, setRoomVisibilityFilter }) => {
    const [open, setOpen] = useState(false);
    const isFiltered = roomVisibilityFilter !== 'all';

    const handleSelect = (value) => {
        if (typeof setRoomVisibilityFilter === 'function') {
            setRoomVisibilityFilter(value);
        }
        setOpen(false);
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`p-2 rounded-lg border transition-colors ${isFiltered
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                aria-label="Room visibility filter"
                title="Filter rooms by live visibility"
                aria-expanded={open}
            >
                <RoomFilterIcon />
            </button>
            {open && (
                <>
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                        {ROOM_VISIBILITY_OPTIONS.map((option) => {
                            const isSelected = roomVisibilityFilter === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${isSelected
                                        ? 'bg-blue-50 text-blue-900 font-medium'
                                        : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                </>
            )}
        </div>
    );
};

const getDraftPeriodId = (draft) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const draftMatchesPeriod = (draft, periodId) => {
    if (periodId === '__unassigned__') {
        return getDraftPeriodId(draft) === null;
    }
    const draftPeriodId = getDraftPeriodId(draft);
    if (!draftPeriodId) {
        return false;
    }
    return String(draftPeriodId) === String(periodId);
};

const LivePeriodIndicator = ({ className = '' }) => (
    <div
        className={`w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0 ${className}`}
        title="Live for this period"
        aria-hidden="true"
    />
);

const getDraftId = (draft) => {
    const raw = draft?.id;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getPeriodActiveDraftId = (period) => {
    if (!period) return null;
    const raw = period.active_schedule_draft_id ?? period.activeScheduleDraftId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const formatPeriodOptionLabel = (period) => {
    if (!period) return '—';
    return period.name || '—';
};

const formatPeriodOptionTitle = (period) => {
    if (!period) return undefined;
    const startWeek = period.start_week ?? period.startWeek;
    const endWeek = period.end_week ?? period.endWeek;
    if (startWeek != null && endWeek != null) {
        return `${period.name} (W${startWeek}–${endWeek})`;
    }
    return period.name;
};

// --- Internal Draft Mode Dropdown Component ---
const DraftModeDropdown = ({
    currentDraftName,
    draftManager,
    onModeChange,
    onDraftChange,
    timePeriods = [],
    selectedPeriodId,
    selectedYearForView,
    setError,
    setSuccess,
    setIsLoading
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDraftDropdownOpen, setIsDraftDropdownOpen] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [newName, setNewName] = useState('');
    const [drafts, setDrafts] = useState([]);
    const [loadingDrafts, setLoadingDrafts] = useState(false);
    const [filterPeriodId, setFilterPeriodId] = useState(() => {
        if (selectedPeriodId != null && selectedPeriodId !== '') {
            return String(selectedPeriodId);
        }
        return timePeriods[0]?.id != null ? String(timePeriods[0].id) : '';
    });

    const loadDrafts = async () => {
        if (!draftManager) return;
        setLoadingDrafts(true);
        try {
            const draftList = await draftManager.fetchDrafts();
            setDrafts(draftList || []);
        } catch (e) {
            console.error('Error loading drafts:', e);
        } finally {
            setLoadingDrafts(false);
        }
    };

    useEffect(() => {
        loadDrafts();
    }, [draftManager]);

    useEffect(() => {
        if (!draftManager) return undefined;

        const handleDraftManagerChange = (state) => {
            if (state.drafts) {
                setDrafts(state.drafts || []);
            }
        };

        draftManager.addListener(handleDraftManagerChange);
        return () => draftManager.removeListener(handleDraftManagerChange);
    }, [draftManager]);

    useEffect(() => {
        if (!currentDraftName) {
            if (selectedPeriodId != null && selectedPeriodId !== '') {
                setFilterPeriodId(String(selectedPeriodId));
            }
            return;
        }

        const currentDraft = drafts.find((d) => d.draft_name === currentDraftName);
        const draftPeriodId = getDraftPeriodId(currentDraft);
        if (draftPeriodId != null) {
            setFilterPeriodId(String(draftPeriodId));
            return;
        }

        if (selectedPeriodId != null && selectedPeriodId !== '') {
            setFilterPeriodId(String(selectedPeriodId));
        }
    }, [currentDraftName, drafts, selectedPeriodId]);

    const hasUnscopedDrafts = useMemo(
        () => drafts.some((draft) => getDraftPeriodId(draft) === null),
        [drafts]
    );

    const periodOptions = useMemo(() => {
        const options = (timePeriods || []).map((period) => ({
            id: String(period.id),
            label: formatPeriodOptionLabel(period)
        }));
        if (hasUnscopedDrafts) {
            options.push({ id: '__unassigned__', label: 'Unscoped drafts' });
        }
        return options;
    }, [timePeriods, hasUnscopedDrafts]);

    const visibleDrafts = useMemo(
        () => drafts.filter((draft) => !isHiddenFromVersions(draft)),
        [drafts]
    );

    const filteredDrafts = useMemo(() => {
        if (!filterPeriodId) return [];
        return visibleDrafts.filter((draft) => draftMatchesPeriod(draft, filterPeriodId));
    }, [visibleDrafts, filterPeriodId]);

    const currentDraft = useMemo(
        () => drafts.find((d) => d.draft_name === currentDraftName) || null,
        [drafts, currentDraftName]
    );
    const currentDraftLabel = formatDraftOptionLabel(currentDraft) || currentDraftName || '';

    const activeDraftIdForFilterPeriod = useMemo(() => {
        if (filterPeriodId === '__unassigned__') return null;
        const period = (timePeriods || []).find((p) => String(p.id) === String(filterPeriodId));
        return getPeriodActiveDraftId(period);
    }, [filterPeriodId, timePeriods]);

    const isDraftActiveForPeriod = useCallback((draft) => {
        const draftId = getDraftId(draft);
        if (!draftId || !activeDraftIdForFilterPeriod) return false;
        return String(draftId) === String(activeDraftIdForFilterPeriod);
    }, [activeDraftIdForFilterPeriod]);

    const isCurrentDraftActiveForFilterPeriod = useMemo(() => {
        if (!currentDraftName) return false;
        const currentDraft = drafts.find((d) => d.draft_name === currentDraftName);
        return currentDraft ? isDraftActiveForPeriod(currentDraft) : false;
    }, [currentDraftName, drafts, isDraftActiveForPeriod]);

    const handleLoadDraft = async (draftName) => {
        if (!draftName || draftName === currentDraftName) return;

        if (setIsLoading) setIsLoading(true);
        try {
            if (draftManager) {
                draftManager.setDraftMode(draftName);
            }
            onModeChange(false, draftName);

            const draft = drafts.find((d) => d.draft_name === draftName);
            const draftPeriodId = getDraftPeriodId(draft);
            if (draftPeriodId != null) {
                setFilterPeriodId(String(draftPeriodId));
            } else {
                setFilterPeriodId('__unassigned__');
            }

            if (setSuccess) setSuccess(`Loaded draft "${formatDraftOptionLabel(draft) || draftName}"`);
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);
            setIsMenuOpen(false);
            setIsDraftDropdownOpen(false);
        } catch (err) {
            if (setError) setError('Failed to load draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleFilterPeriodChange = (e) => {
        const nextPeriodId = e.target.value;
        if (!nextPeriodId) return;

        setFilterPeriodId(nextPeriodId);

        const matchingDrafts = visibleDrafts.filter((draft) => draftMatchesPeriod(draft, nextPeriodId));
        const currentDraftInList = currentDraftName
            && matchingDrafts.some((draft) => draft.draft_name === currentDraftName);

        if (!currentDraftInList && matchingDrafts.length > 0) {
            handleLoadDraft(matchingDrafts[0].draft_name);
        }
    };

    const handleRename = async () => {
        if (!newName.trim()) {
            if (setError) setError('Please enter a new draft name');
            return;
        }

        const currentTitle = getDraftTitle(currentDraft) || currentDraftName;
        if (newName.trim() === currentTitle) {
            setIsRenaming(false);
            setNewName('');
            return;
        }

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.renameDraft(currentDraftName, newName.trim());

            if (setSuccess) setSuccess(`Draft renamed to "${newName.trim()}"`);
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);

            setIsRenaming(false);
            setNewName('');
            setIsMenuOpen(false);
        } catch (err) {
            if (setError) setError('Failed to rename draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const selectedDraftInFilter = filteredDrafts.some((d) => d.draft_name === currentDraftName);

    const selectedFilterPeriod = filterPeriodId === '__unassigned__'
        ? null
        : (timePeriods || []).find((p) => String(p.id) === String(filterPeriodId));

    return (
        <div className="flex items-center gap-2">
            <div className="relative my-2">
                <select
                    value={filterPeriodId}
                    onChange={handleFilterPeriodChange}
                    disabled={periodOptions.length === 0}
                    title={
                        filterPeriodId === '__unassigned__'
                            ? 'Unscoped drafts'
                            : formatPeriodOptionTitle(selectedFilterPeriod)
                    }
                    className="appearance-none pl-3 pr-8 py-2.5 min-w-[10rem] max-w-[14rem] bg-transparent text-gray-800 rounded-2xl border border-gray-300 text-sm font-medium truncate focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 hover:border-gray-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                >
                    {periodOptions.length === 0 ? (
                        <option value="">No periods</option>
                    ) : (
                        periodOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))
                    )}
                </select>
                <IoChevronDown
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none"
                    aria-hidden="true"
                />
            </div>

            <div className="relative flex items-center gap-1 self-stretch">
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setIsDraftDropdownOpen((open) => !open)}
                        disabled={loadingDrafts}
                        className="flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-lg border border-blue-200 hover:bg-blue-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-w-[10rem]"
                        title={currentDraftLabel || 'Select draft'}
                    >
                        {isCurrentDraftActiveForFilterPeriod ? (
                            <LivePeriodIndicator />
                        ) : (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" aria-hidden="true" />
                        )}
                        <span className="text-xs font-medium text-blue-900">Draft</span>
                        <span className="font-semibold text-sm truncate max-w-[14rem]">
                            {loadingDrafts
                                ? 'Loading...'
                                : (currentDraftLabel || (filteredDrafts.length === 0 ? 'No drafts' : 'Select draft'))}
                        </span>
                        <IoChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isDraftDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDraftDropdownOpen && (
                        <>
                            <div className="absolute top-full left-0 mt-1 min-w-[16rem] max-w-[20rem] bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 max-h-60 overflow-auto">
                                {!selectedDraftInFilter && currentDraftName ? (
                                    <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                                        Current: {currentDraftLabel} (other period)
                                    </div>
                                ) : null}
                                {filteredDrafts.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">No drafts for this period</div>
                                ) : (
                                    filteredDrafts.map((draft) => {
                                        const isActive = isDraftActiveForPeriod(draft);
                                        const isSelected = draft.draft_name === currentDraftName;
                                        const label = formatDraftOptionLabel(draft);
                                        return (
                                            <button
                                                key={draft.draft_name}
                                                type="button"
                                                onClick={() => handleLoadDraft(draft.draft_name)}
                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${isSelected ? 'bg-blue-50 text-blue-900' : 'text-gray-700'}`}
                                                title={isActive ? `${label} (live for this period)` : label}
                                            >
                                                {isActive ? (
                                                    <LivePeriodIndicator />
                                                ) : (
                                                    <span className="w-2 h-2 flex-shrink-0" aria-hidden="true" />
                                                )}
                                                <span className="truncate font-medium">{label}</span>
                                                {isActive && (
                                                    <span className="ml-auto text-xs text-green-700 font-medium flex-shrink-0">
                                                        Live
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                            <div
                                className="fixed inset-0 z-40"
                                onClick={() => setIsDraftDropdownOpen(false)}
                            />
                        </>
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="p-1 rounded-md border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
                    title="Draft actions"
                    aria-label="Draft actions"
                >
                    <IoChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isMenuOpen && (
                    <>
                        <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            <div className="py-1">
                                <button
                                    onClick={() => {
                                        setIsRenaming(true);
                                        setNewName(getDraftTitle(currentDraft) || currentDraftName || '');
                                    }}
                                    disabled={!currentDraftName}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                                >
                                    Rename
                                </button>
                            </div>
                        </div>
                        <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                    </>
                )}
            </div>

            {/* Rename Modal */}
            {isRenaming && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96">
                        <h3 className="text-lg font-semibold mb-4">Rename Draft</h3>
                        <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Enter new draft name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                            autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => {
                                    setIsRenaming(false);
                                    setNewName('');
                                }}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRename}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Rename
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
